import { useEffect, useId, useMemo, useState } from 'react';
import type { BrandProfileView, Tenant } from '@mg/shared';
import { UnsavedChangesDialog } from '../../components/ui/UnsavedChangesDialog';
import { ErrorState, LoadingState } from '../../components/ui/States';
import { useBrandProfile } from '../brand/queries';
import { BrandDraftProvider } from './draft/BrandDraftContext';
import { ExitOnboardingDialog } from './ExitOnboardingDialog';
import { ImportBanner } from './import/ImportBanner';
import { RevisitBanner } from './revisit/RevisitBanner';
import { RevisitProvider } from './revisit/RevisitProvider';
import { StepCard } from './StepCard';
import { StepPills } from './StepPills';
import { CompanyStep } from './step2/CompanyStep';
import { VocabularyStep } from './step5/VocabularyStep';
import { GuidelinesStep } from './step6/GuidelinesStep';
import { AudiencesStep } from './step7/AudiencesStep';
import { StartStep } from './step1/StartStep';
import { ALL_STEPS_DONE, stepDescription, stepTitle } from './steps';
import { FieldsStep } from './steps/FieldsStep';
import { SuggestionsProvider } from './suggestions/SuggestionsProvider';
import { useWizardController, type WizardController } from './useWizardController';
import { WizardBar } from './WizardBar';
import { WizardFrame } from './WizardFrame';
import { WizardHeader } from './WizardHeader';
import { LOAD_FAILED, WIZARD_UNSAVED_MESSAGE } from './wizardCopy';

/**
 * Brand Setup for one workspace (web.md §4): its brand's details are read once, then the wizard runs on
 * that copy, saving one step at a time. A read that has not finished yet (the cached details being
 * checked again) is waited for, so the first save never sends a version older than the server's.
 */
export function BrandSetupWizard({ tenant }: { tenant: Tenant }) {
  const profile = useBrandProfile(tenant.id);
  const [initialView, setInitialView] = useState<BrandProfileView | null>(null);

  useEffect(() => {
    if (initialView === null && profile.data && !profile.isFetching) setInitialView(profile.data);
  }, [initialView, profile.data, profile.isFetching]);

  if (initialView) {
    return (
      <WizardSession
        tenant={tenant}
        initialView={initialView}
        liveView={profile.data ?? initialView}
        reloadProfile={profile.refetch}
      />
    );
  }
  if (profile.isError && !profile.isFetching) {
    return (
      <WizardFrame>
        <ErrorState message={LOAD_FAILED} onRetry={() => void profile.refetch()} />
      </WizardFrame>
    );
  }
  return (
    <WizardFrame>
      <LoadingState />
    </WizardFrame>
  );
}

function WizardSession({
  tenant,
  initialView,
  liveView,
  reloadProfile,
}: {
  tenant: Tenant;
  initialView: BrandProfileView;
  /** The cached view as saves update it: the done pills. */
  liveView: BrandProfileView;
  reloadProfile: ReturnType<typeof useBrandProfile>['refetch'];
}) {
  const titleId = useId();
  const c = useWizardController({ tenant, initialView, reloadProfile });
  const draft = useMemo(() => ({ state: c.state, dispatch: c.dispatch }), [c.state, c.dispatch]);
  const done = c.mode === 'revisit' ? ALL_STEPS_DONE : liveView.onboarding.savedSteps;
  const job = c.imports.job;

  return (
    <BrandDraftProvider value={draft}>
      <SuggestionsProvider>
        <RevisitProvider>
          <WizardFrame
            bar={
              <WizardBar
                step={c.step}
                busy={c.barBusy}
                locked={c.busy && c.barBusy === null}
                error={c.stepError}
                onBack={c.back}
                onExit={c.openExit}
                onSkip={c.skip}
                onNext={c.next}
                onReload={c.reload}
              />
            }
          >
            <WizardHeader mode={c.mode} disabled={c.busy} onExit={c.openExit} />
            <RevisitBanner disabled={c.busy} />
            {c.step > 1 && c.importOn && job && (
              <ImportBanner
                job={job}
                timedOut={c.imports.timedOut}
                workspaceName={tenant.name}
                overflow={c.state.importOverflow}
                retrying={c.imports.retrying}
                retryError={c.imports.retryError}
                onRetry={() => void c.imports.retry()}
                onChooseAnotherFile={() => c.jump(1)}
              />
            )}
            <StepPills step={c.step} done={done} disabled={c.busy} onSelect={c.jump} />
            <StepCard
              step={c.step}
              titleId={titleId}
              title={stepTitle(c.step, c.mode)}
              description={stepDescription(c.step, c.mode)}
            >
              <StepContent c={c} tenantId={tenant.id} titleId={titleId} />
            </StepCard>
          </WizardFrame>

          <ExitOnboardingDialog
            open={c.exitOpen}
            busy={c.exitBusy}
            error={c.exitError}
            onSaveAndExit={c.saveAndExit}
            onExitWithoutSaving={c.exitWithoutSaving}
            onContinue={c.closeExit}
            returnFocusTo={c.exitOpener.current}
          />
          <UnsavedChangesDialog
            open={c.leaveIntent !== null}
            message={WIZARD_UNSAVED_MESSAGE}
            saving={c.leaveBusy}
            canSave={c.check.ok}
            onKeepEditing={c.keepEditing}
            onDiscard={c.leaveDiscarding}
            onSave={c.leaveSaving}
            returnFocusTo={c.leaveOpener.current}
          />
          {c.guard}
        </RevisitProvider>
      </SuggestionsProvider>
    </BrandDraftProvider>
  );
}

/** The current step's fields. Keyed by step, so a step's own state (a copy notice, open cards) starts fresh. */
function StepContent({ c, tenantId, titleId }: { c: WizardController; tenantId: string; titleId: string }) {
  const disabled = c.busy;
  switch (c.step) {
    case 1:
      return (
        <StartStep
          mode={c.mode}
          titleId={titleId}
          disabled={disabled}
          pendingFile={c.pendingFile}
          fileError={c.fileError}
          uploading={c.uploading}
          uploadProgress={c.uploadProgress}
          currentDocument={c.imports.job?.document ?? null}
          pages={c.imports.job?.pages}
          urls={c.urls}
          onUrlsChange={c.setUrls}
          onChooseFile={c.chooseFile}
          onRemoveFile={c.removeFile}
          revisitChoice={c.revisitChoice}
          onRevisitChoice={c.setRevisitChoice}
          revisitSources={{
            job: c.sourcesJob,
            loading: c.sourcesLoading,
            loadFailed: c.sourcesLoadFailed,
            removedDocument: c.removedDocument,
            onRemoveDocument: c.removeDocument,
            onKeepDocument: c.keepDocument,
            pageDraft: c.pageDraft,
            onPageDraftChange: c.setPageDraft,
            retryingPages: c.retryingPages,
            onRetryPage: c.retryPage,
          }}
        />
      );
    case 2:
      return <CompanyStep key={2} tenantId={tenantId} disabled={disabled} />;
    case 5:
      return <VocabularyStep key={5} disabled={disabled} />;
    case 6:
      return <GuidelinesStep key={6} disabled={disabled} />;
    case 7:
      return <AudiencesStep key={7} disabled={disabled} />;
    default:
      return <FieldsStep key={c.step} step={c.step} disabled={disabled} />;
  }
}
